import { getHoleStrokes } from "./strokes";

/**
 * Mirror of apps/api/src/util/segments.ts. Kept in step by
 * apps/api/src/util/scores.parity.spec.ts.
 *
 * Formats and segments arrive as plain shapes rather than the API's enum and
 * entity, so this stays usable wherever the event arrives — the same reason
 * getTeamStrokes takes a string mode.
 */

/** Who owns the score rows for a segment — one ball per team, or one each. */
export type SegmentOwner = "TEAM" | "PLAYER";

export type SegmentHole = {
  number: number;
  index: number;
};

export type Segment = {
  fromHole: number;
  toHole: number;
  format: string;
};

/**
 * One ball per team means the TEAM owns the row; best ball and singles both
 * track every player's own ball.
 */
export const segmentOwner = (format: string): SegmentOwner =>
  format === "SCRAMBLE" || format === "FOURSOME" ? "TEAM" : "PLAYER";

/**
 * Whether a block's MATCH is played between teams rather than individuals.
 *
 * Deliberately not segmentOwner. That answers who owns the score ROWS, and
 * best ball answers "PLAYER" there while its match side is still the TEAM,
 * playing the better of the two. Only a singles block pairs individuals.
 */
export const segmentSidesAreTeams = (format: string): boolean =>
  format !== "GOLF";

/**
 * Match points a block is worth to a flight.
 *
 * A team block is one match; a singles block is TWO, each partner against
 * their opposite number. So a flight is worth 4 points when a singles block is
 * in play and 3 when all three are team-based.
 */
export const segmentMatchPoints = (format: string): number =>
  segmentSidesAreTeams(format) ? 1 : 2;

export const segmentForHole = <T extends Segment>(
  segments: readonly T[] | null | undefined,
  holeNumber: number,
): T | null =>
  segments?.find(
    (segment) => holeNumber >= segment.fromHole && holeNumber <= segment.toHole,
  ) ?? null;

/**
 * The hole's difficulty rank WITHIN its segment, 1..n.
 *
 * A segment holds six of the card's eighteen holes, so their course stroke
 * indexes are six arbitrary numbers out of 1..18 — useless as an allocation
 * order on their own. Ranking them among themselves restores the 1..n the
 * allocator expects.
 */
export const segmentHoleRank = (
  holes: readonly SegmentHole[],
  segment: Segment,
  holeNumber: number,
): number | null => {
  const inSegment = holes
    .filter((h) => h.number >= segment.fromHole && h.number <= segment.toHole)
    .sort((a, b) => a.index - b.index);

  const rank = inSegment.findIndex((h) => h.number === holeNumber);
  return rank === -1 ? null : rank + 1;
};

/**
 * Scale a whole-round allowance to one segment.
 *
 * Pro-rata, NOT the ordinary 18-hole rule of spreading by index and keeping
 * whatever lands on the six holes. Stroke-index cards are conventionally
 * balanced across the two nines but nothing balances them across thirds, so
 * the ordinary rule hands a team 3 strokes if its scramble sits on holes 1-6
 * and 0 if it sits on 7-12 — a third of the event played gross by accident of
 * the card.
 */
export const segmentAllowance = (
  allowance: number,
  segmentCount: number,
): number => (segmentCount > 0 ? Math.round(allowance / segmentCount) : 0);

/**
 * The entrant's strokes on one hole of a segment.
 *
 * `allowance` is the entrant's whole-round figure — a team's format weighting
 * for scramble and foursome, a player's playing handicap for best ball and
 * singles.
 */
export const segmentHoleStrokes = (
  allowance: number,
  holes: readonly SegmentHole[],
  segments: readonly Segment[],
  holeNumber: number,
): number => {
  const segment = segmentForHole(segments, holeNumber);
  if (!segment) return 0;

  const rank = segmentHoleRank(holes, segment, holeNumber);
  if (!rank) return 0;

  const holeCount = holes.filter(
    (h) => h.number >= segment.fromHole && h.number <= segment.toHole,
  ).length;

  return getHoleStrokes(
    segmentAllowance(allowance, segments.length),
    rank,
    holeCount,
  );
};
